import type { ApiClient } from '../apiClient';

export interface PasskeyInfo {
  id: string;
  nickname: string | null;
  rp_id: string;
  last_used_at: string | null;
  created_at: string;
}

export interface TrustedDeviceInfo {
  id: string;
  label: string | null;
  last_used_at: string | null;
  expires_at: string;
}

export interface MfaStatus {
  totp_enabled: boolean;
  recovery_codes_remaining: number;
  passkeys: PasskeyInfo[];
  trusted_devices?: TrustedDeviceInfo[];
}

export interface TotpSetupResult {
  otpauth_uri: string;
  qr_svg: string;
}

export interface MfaApi {
  status(): Promise<MfaStatus>;
  totpSetup(password: string): Promise<TotpSetupResult>;
  totpConfirm(password: string, code: string): Promise<{ recovery_codes: string[] }>;
  disable(code: string): Promise<void>;
  regenerateRecoveryCodes(password: string): Promise<{ recovery_codes: string[] }>;
  deletePasskey(id: string, password: string): Promise<void>;
  deleteTrustedDevice(id: string, password: string): Promise<void>;
}

// MFA/passkey management endpoint calls. Browser-only; the ApiClient is injected
// (never an app's singleton) so both webclient and adminapp reuse this untouched.
// Login-completion calls (mfa/verify, passwordless finish) live elsewhere because
// they carry app-specific concerns (e.g. webclient's guest-cart token).
export function createMfaApi(client: ApiClient, base = '/direct/usersvc'): MfaApi {
  return {
    status: () => client.get<MfaStatus>(`${base}/auth/mfa/status`),
    totpSetup: (password) => client.post<TotpSetupResult>(`${base}/auth/mfa/totp/setup`, { password }),
    totpConfirm: (password, code) =>
      client.post<{ recovery_codes: string[] }>(`${base}/auth/mfa/totp/confirm`, { password, code }),
    disable: (code) => client.post<void>(`${base}/auth/mfa/disable`, { code }),
    regenerateRecoveryCodes: (password) =>
      client.post<{ recovery_codes: string[] }>(`${base}/auth/mfa/recovery-codes/regenerate`, { password }),
    deletePasskey: (id, password) => client.delete<void>(`${base}/auth/mfa/webauthn/${id}`, { password }),
    deleteTrustedDevice: (id, password) =>
      client.delete<void>(`${base}/auth/mfa/trusted-devices/${id}`, { password }),
  };
}
